'use client'

import Image from 'next/image'
import Link from 'next/link'
import { useState, useMemo } from 'react'
import type { ExchangeType, FeesTier, RegionRestriction } from '@/lib/exchangesMeta'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ExchangeRow {
  id: string
  name: string
  image: string
  trustScore: number
  trustScoreRank: number
  volume24hBtc: number
  volume24hUsd: number
  yearEstablished: number | null
  country: string | null
  url: string
  type: ExchangeType
  feesTier: FeesTier
  makerFee: number
  takerFee: number
  kycRequired: boolean
  derivatives: boolean
  regionRestriction: RegionRestriction
  featured: boolean
  referralBonus?: string
  referralUrl?: string
}

type SortKey = 'trustScoreRank' | 'volume24hUsd' | 'makerFee' | 'yearEstablished' | 'trustScore'
type SortDir = 'asc' | 'desc'
type FilterType = 'all' | 'cex' | 'dex'
type FilterRegion = 'all' | 'global' | 'us-restricted'
type FilterDerivatives = 'all' | 'yes' | 'no'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const sf = "var(--font-saira), 'Saira', sans-serif"

const MUTED = '#a1a1aa'

function formatVolume(usd: number): string {
  if (usd >= 1_000_000_000) return `$${(usd / 1_000_000_000).toFixed(1)}B`
  if (usd >= 1_000_000) return `$${(usd / 1_000_000).toFixed(0)}M`
  return `$${(usd / 1_000).toFixed(0)}K`
}

function SortIcon({ active, direction }: { active: boolean; direction: SortDir }) {
  return (
    <svg
      width="10" height="14" viewBox="0 0 10 14"
      className={`ml-1 inline-block transition-opacity ${active ? 'opacity-100' : 'opacity-0 group-hover:opacity-40'}`}
    >
      <path d="M5 0L9.5 5.5H0.5L5 0Z" fill={active && direction === 'asc' ? '#a1a1aa' : '#52525b'} />
      <path d="M5 14L0.5 8.5H9.5L5 14Z" fill={active && direction === 'desc' ? '#a1a1aa' : '#52525b'} />
    </svg>
  )
}

function TrustBar({ score }: { score: number }) {
  return (
    <div style={{ width: 80, marginTop: 5 }}>
      <div style={{ background: '#1c1c1c', height: 3, width: '100%' }}>
        <div style={{ background: '#52525b', height: '100%', width: `${(score / 10) * 100}%` }} />
      </div>
    </div>
  )
}

// ─── Featured card ────────────────────────────────────────────────────────────

function FeaturedCard({ exchange }: { exchange: ExchangeRow }) {
  return (
    <Link
      href={exchange.referralUrl ?? `/exchanges/${exchange.id}`}
      target={exchange.referralUrl ? '_blank' : undefined}
      rel={exchange.referralUrl ? 'noopener noreferrer' : undefined}
      className="group block overflow-hidden transition-all"
      style={{ border: '1px solid #27272a', background: '#000', textDecoration: 'none' }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = '#3f3f46')}
      onMouseLeave={e => (e.currentTarget.style.borderColor = '#27272a')}
    >
      <div
        className="flex items-center justify-between"
        style={{ padding: '14px 18px 12px', borderBottom: '1px solid #18181b' }}
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 relative flex-shrink-0">
            <Image src={exchange.image} alt={exchange.name} fill className="object-contain rounded" unoptimized />
          </div>
          <span className="text-white font-bold" style={{ fontFamily: sf, fontSize: 14 }}>
            {exchange.name}
          </span>
          <span style={{ background: '#18181b', color: '#a1a1aa', fontFamily: sf, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, padding: '2px 6px' }}>
            {exchange.type}
          </span>
        </div>
        <span style={{ color: MUTED, fontFamily: sf, fontSize: 11, fontWeight: 700 }}>
          #{exchange.trustScoreRank}
        </span>
      </div>

      <div className="flex items-start gap-6" style={{ padding: '14px 18px 14px' }}>
        <div>
          <div style={{ color: MUTED, fontFamily: sf, fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>
            24h volume
          </div>
          <div
            className="tabular-nums group-hover:text-white transition-colors"
            style={{ color: '#e4e4e7', fontFamily: sf, fontSize: 28, fontWeight: 800, letterSpacing: -1, lineHeight: 1 }}
          >
            {formatVolume(exchange.volume24hUsd)}
          </div>
        </div>
        <div>
          <div style={{ color: MUTED, fontFamily: sf, fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 6 }}>
            Maker / taker
          </div>
          <div className="tabular-nums" style={{ color: '#a1a1aa', fontFamily: sf, fontSize: 18, fontWeight: 700, lineHeight: 1 }}>
            {exchange.makerFee}% / {exchange.takerFee}%
          </div>
        </div>
      </div>

      {exchange.referralBonus && (
        <div
          className="flex items-center justify-between"
          style={{ background: 'rgba(34,197,94,0.05)', borderTop: '1px solid rgba(34,197,94,0.15)', padding: '10px 18px' }}
        >
          <span style={{ color: '#22c55e', fontFamily: sf, fontSize: 12, fontWeight: 700 }}>
            {exchange.referralBonus}
          </span>
          <span style={{ border: '1px solid rgba(34,197,94,0.4)', color: '#22c55e', fontFamily: sf, fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, padding: '4px 10px' }}>
            Claim →
          </span>
        </div>
      )}
    </Link>
  )
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ExchangesClient({ exchanges }: { exchanges: ExchangeRow[] }) {
  const [sortKey, setSortKey] = useState<SortKey>('trustScoreRank')
  const [sortDir, setSortDir] = useState<SortDir>('asc')
  const [filterType, setFilterType] = useState<FilterType>('all')
  const [filterRegion, setFilterRegion] = useState<FilterRegion>('all')
  const [filterDerivatives, setFilterDerivatives] = useState<FilterDerivatives>('all')

  const FEATURED_IDS = ['bitget', 'bingx']
  const featured = useMemo(
    () => FEATURED_IDS.map(id => (exchanges ?? []).find(e => e.id === id)).filter(Boolean) as ExchangeRow[],
    [exchanges]
  )

  const filtered = useMemo(() => {
    let rows = [...(exchanges ?? [])]
    if (filterType !== 'all') rows = rows.filter(e => e.type === filterType)
    if (filterRegion === 'global') rows = rows.filter(e => e.regionRestriction === 'global')
    if (filterRegion === 'us-restricted') rows = rows.filter(e => e.regionRestriction === 'us-restricted')
    if (filterDerivatives === 'yes') rows = rows.filter(e => e.derivatives)
    if (filterDerivatives === 'no') rows = rows.filter(e => !e.derivatives)
    rows.sort((a, b) => {
      const aVal = (a[sortKey] ?? 0) as number
      const bVal = (b[sortKey] ?? 0) as number
      return sortDir === 'asc' ? aVal - bVal : bVal - aVal
    })
    return rows
  }, [exchanges, filterType, filterRegion, filterDerivatives, sortKey, sortDir])

  function toggleSort(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortKey(key)
      setSortDir(key === 'trustScoreRank' || key === 'makerFee' ? 'asc' : 'desc')
    }
  }

  const btnBase: React.CSSProperties = { fontFamily: sf, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 2, padding: '5px 10px', cursor: 'pointer', border: 'none', transition: 'all 0.15s' }
  const btnActive: React.CSSProperties = { ...btnBase, background: '#27272a', color: '#fff' }
  const btnInactive: React.CSSProperties = { ...btnBase, background: 'transparent', color: MUTED, fontWeight: 400 }
  const segTrack: React.CSSProperties = { display: 'flex', background: '#0a0a0a', border: '1px solid #27272a' }

  return (
    <main className="bg-black min-h-screen">
      <div className="max-w-6xl mx-auto px-4 py-12">

        <div className="mb-8">
          <h1 className="text-white mb-2 font-[family-name:var(--font-saira)]" style={{ fontSize: 32, fontWeight: 600 }}>
            Crypto exchanges
          </h1>
          <p className="text-zinc-400 text-sm max-w-2xl leading-relaxed">
            A hand-picked selection of exchanges we vet for security, fees, liquidity, and track record. Not every exchange that exists - only the ones worth your time.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
          {featured.map(e => <FeaturedCard key={e.id} exchange={e} />)}
        </div>

        <div className="border border-zinc-800 overflow-hidden">

          <div className="border-b border-zinc-800 bg-black">

            <div className="hidden sm:flex items-center justify-between px-4 py-3 gap-6">
              <div className="flex items-center gap-5">
                <div className="flex items-center gap-2">
                  <span style={{ color: MUTED, fontFamily: sf, fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, whiteSpace: 'nowrap' }}>Type</span>
                  <div style={segTrack}>
                    {([['all', 'All'], ['cex', 'CEX'], ['dex', 'DEX']] as const).map(([val, label]) => (
                      <button key={val} onClick={() => setFilterType(val)} style={filterType === val ? btnActive : btnInactive}>{label}</button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ color: MUTED, fontFamily: sf, fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, whiteSpace: 'nowrap' }}>Region</span>
                  <div style={segTrack}>
                    {([['all', 'All'], ['global', 'Global'], ['us-restricted', 'No US']] as const).map(([val, label]) => (
                      <button key={val} onClick={() => setFilterRegion(val)} style={filterRegion === val ? btnActive : btnInactive}>{label}</button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span style={{ color: MUTED, fontFamily: sf, fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, whiteSpace: 'nowrap' }}>Products</span>
                  <div style={segTrack}>
                    {([['all', 'All'], ['yes', 'Derivatives'], ['no', 'Spot']] as const).map(([val, label]) => (
                      <button key={val} onClick={() => setFilterDerivatives(val)} style={filterDerivatives === val ? btnActive : btnInactive}>{label}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span style={{ color: MUTED, fontFamily: sf, fontSize: 11 }}>{filtered.length} exchanges</span>
              </div>
            </div>

            <div className="flex sm:hidden flex-col divide-y divide-zinc-900">
              <div className="flex items-center gap-3 px-4 py-2.5 overflow-x-auto">
                <span style={{ color: MUTED, fontFamily: sf, fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, flexShrink: 0 }}>Type</span>
                <div style={{ ...segTrack, flexShrink: 0 }}>
                  {([['all', 'All'], ['cex', 'CEX'], ['dex', 'DEX']] as const).map(([val, label]) => (
                    <button key={val} onClick={() => setFilterType(val)} style={{ ...filterType === val ? btnActive : btnInactive, letterSpacing: 1 }}>{label}</button>
                  ))}
                </div>
                <span style={{ color: MUTED, fontFamily: sf, fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, flexShrink: 0, marginLeft: 4 }}>Products</span>
                <div style={{ ...segTrack, flexShrink: 0 }}>
                  {([['all', 'All'], ['yes', 'Deriv.'], ['no', 'Spot']] as const).map(([val, label]) => (
                    <button key={val} onClick={() => setFilterDerivatives(val)} style={{ ...filterDerivatives === val ? btnActive : btnInactive, letterSpacing: 1 }}>{label}</button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-3 px-4 py-2.5 overflow-x-auto">
                <span style={{ color: MUTED, fontFamily: sf, fontSize: 9, textTransform: 'uppercase', letterSpacing: 2, fontWeight: 700, flexShrink: 0 }}>Region</span>
                <div style={{ ...segTrack, flexShrink: 0 }}>
                  {([['all', 'All'], ['global', 'Global'], ['us-restricted', 'No US']] as const).map(([val, label]) => (
                    <button key={val} onClick={() => setFilterRegion(val)} style={{ ...filterRegion === val ? btnActive : btnInactive, letterSpacing: 1 }}>{label}</button>
                  ))}
                </div>
                <span style={{ color: MUTED, fontFamily: sf, fontSize: 11, flexShrink: 0 }}>{filtered.length} exchanges</span>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full" style={{ minWidth: 600 }}>
              <thead>
                <tr className="border-b border-zinc-800 bg-black">
                  <th className="px-4 py-4 text-left uppercase tracking-widest text-zinc-400 font-medium cursor-pointer select-none w-10" style={{ fontFamily: sf, fontSize: 11 }} onClick={() => toggleSort('trustScoreRank')}>
                    <span className="inline-flex items-center"># <SortIcon active={sortKey === 'trustScoreRank'} direction={sortDir} /></span>
                  </th>
                  <th className="px-4 py-4 text-left uppercase tracking-widest text-zinc-400 font-medium" style={{ fontFamily: sf, fontSize: 11 }}>Exchange</th>
                  <th className="px-4 py-4 text-left uppercase tracking-widest text-zinc-400 font-medium cursor-pointer select-none" style={{ fontFamily: sf, fontSize: 11 }} onClick={() => toggleSort('trustScore')}>
                    <span className="inline-flex items-center">Trust <SortIcon active={sortKey === 'trustScore'} direction={sortDir} /></span>
                  </th>
                  <th className="px-4 py-4 text-right uppercase tracking-widest text-zinc-400 font-medium cursor-pointer select-none" style={{ fontFamily: sf, fontSize: 11 }} onClick={() => toggleSort('volume24hUsd')}>
                    <span className="inline-flex items-center justify-end w-full">Volume <SortIcon active={sortKey === 'volume24hUsd'} direction={sortDir} /></span>
                  </th>
                  <th className="px-4 py-4 text-right uppercase tracking-widest text-zinc-400 font-medium cursor-pointer select-none" style={{ fontFamily: sf, fontSize: 11 }} onClick={() => toggleSort('makerFee')}>
                    <span className="inline-flex items-center justify-end w-full">Fees <SortIcon active={sortKey === 'makerFee'} direction={sortDir} /></span>
                  </th>
                  <th className="px-4 py-4 text-right uppercase tracking-widest text-zinc-400 font-medium hidden lg:table-cell" style={{ fontFamily: sf, fontSize: 11 }}>KYC</th>
                  <th className="px-4 py-4 text-right uppercase tracking-widest text-zinc-400 font-medium cursor-pointer select-none hidden xl:table-cell" style={{ fontFamily: sf, fontSize: 11 }} onClick={() => toggleSort('yearEstablished')}>
                    <span className="inline-flex items-center justify-end w-full">Founded <SortIcon active={sortKey === 'yearEstablished'} direction={sortDir} /></span>
                  </th>
                  <th className="px-4 py-4 text-right uppercase tracking-widest text-zinc-400 font-medium hidden xl:table-cell" style={{ fontFamily: sf, fontSize: 11 }}>Deriv.</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((exchange) => (
                  <tr key={exchange.id} className="border-b border-zinc-800 hover:bg-zinc-900/50 transition-colors cursor-pointer group/row">
                    <td className="px-4 py-4 text-zinc-400 text-base w-10 tabular-nums" style={{ fontFamily: sf }}>
                      {exchange.trustScoreRank}
                    </td>
                    <td className="px-4 py-4">
                      <Link href={`/exchanges/${exchange.id}`} className="flex items-center gap-3">
                        <div className="w-8 h-8 relative flex-shrink-0">
                          <Image src={exchange.image} alt={exchange.name} fill className="object-contain rounded" unoptimized />
                        </div>
                        <span className="text-white text-base font-medium group-hover/row:text-green-400 transition-colors whitespace-nowrap" style={{ fontFamily: sf }}>
                          {exchange.name}
                        </span>
                        <span className="text-zinc-400 uppercase whitespace-nowrap" style={{ fontFamily: sf, fontSize: 10, letterSpacing: 1 }}>
                          {exchange.type}
                        </span>
                      </Link>
                    </td>
                    <td className="px-4 py-4">
                      <span className="text-white text-sm tabular-nums whitespace-nowrap" style={{ fontFamily: sf }}>
                        {exchange.trustScore}/10
                      </span>
                      <TrustBar score={exchange.trustScore} />
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="text-white text-sm font-medium tabular-nums whitespace-nowrap" style={{ fontFamily: sf }}>
                        {formatVolume(exchange.volume24hUsd)}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <span className="text-zinc-300 text-sm tabular-nums whitespace-nowrap" style={{ fontFamily: sf }}>
                        {exchange.makerFee}% / {exchange.takerFee}%
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right hidden lg:table-cell">
                      <span className={`text-sm ${exchange.kycRequired ? 'text-zinc-400' : 'text-green-500'}`} style={{ fontFamily: sf }}>
                        {exchange.kycRequired ? 'Required' : 'Optional'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right hidden xl:table-cell">
                      <span className="text-zinc-300 text-sm tabular-nums" style={{ fontFamily: sf }}>
                        {exchange.yearEstablished ?? '—'}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right hidden xl:table-cell">
                      <span className={`text-sm ${exchange.derivatives ? 'text-green-500' : 'text-zinc-500'}`} style={{ fontFamily: sf }}>
                        {exchange.derivatives ? 'Yes' : 'No'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filtered.length === 0 && (
            <div className="py-12 text-center text-zinc-400 text-sm bg-black">
              No exchanges match the selected filters
            </div>
          )}
        </div>

        <div className="mt-16 mb-6 max-w-3xl">
          <h2 className="text-zinc-300 text-xl lg:text-3xl font-semibold mb-3 font-[family-name:var(--font-saira)]">
            About crypto exchanges
          </h2>
          <div className="text-zinc-400 text-sm leading-relaxed space-y-3">
            <p>
              Cryptocurrency exchanges are platforms where users buy, sell, and trade digital assets. Centralized exchanges (CEX) operate as intermediaries, requiring KYC verification but offering higher liquidity and more trading pairs.
            </p>
            <p>
              Decentralized exchanges (DEX) operate via smart contracts, allowing permissionless trading without custody of funds.
            </p>
            <p>
              Trust score is calculated by CoinGecko based on web traffic, trading volume legitimacy, cybersecurity practices, and API coverage. Scores range from 1 to 10 - a higher score indicates a more established and transparent exchange. 24h volume figures are normalized to filter out wash trading.
            </p>
            <p>
              This list is curated - not every exchange on the market is here. We include only the exchanges we believe in, based on trust score, security history, fee competitiveness, regulatory standing, and the depth of partnerships and integrations we&apos;ve built with them over time.
            </p>
          </div>

          <h2 className="text-zinc-300 text-xl lg:text-3xl font-semibold mb-3 mt-10 font-[family-name:var(--font-saira)]">
            Crypto exchanges FAQ
          </h2>
          <div className="border border-zinc-800 divide-y divide-zinc-800">
            {[
              {
                q: 'What is the best crypto exchange in 2026?',
                a: 'Binance ranks #1 by trust score in 2026. The best exchange for you depends on your location, trading style, and whether you need derivatives or spot trading only.',
              },
              {
                q: 'Which crypto exchange has the lowest fees in 2026?',
                a: "Binance, Bitget, and OKX offer maker/taker fees starting at 0.1%. Fees can be reduced further by holding the exchange's native token or using a referral code at signup.",
              },
              {
                q: 'Which crypto exchanges do not require KYC?',
                a: 'Decentralized exchanges (DEXs) like Uniswap and dYdX allow trading without KYC. Some centralized exchanges offer limited access without verification, but full withdrawal limits typically require identity checks.',
              },
              {
                q: 'What is a trust score for crypto exchanges?',
                a: 'Trust score is a metric calculated by CoinGecko that evaluates an exchange based on web traffic, trading volume legitimacy, API data quality, and cybersecurity practices. Scores range from 1 to 10, with 10 being the most trusted.',
              },
              {
                q: 'What is the difference between a CEX and a DEX?',
                a: 'A centralized exchange (CEX) is operated by a company that holds your funds and requires identity verification. A decentralized exchange (DEX) runs on smart contracts, letting you trade directly from your own wallet without giving up custody of your assets.',
              },
            ].map(({ q, a }) => (
              <div key={q} className="px-5 py-4">
                <h3 className="text-white text-sm font-bold mb-1.5 font-[family-name:var(--font-saira)]">{q}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{a}</p>
              </div>
            ))}
          </div>
        </div>

      </div>
    </main>
  )
}